/**
 * 작업 현황 — 견적별 상태·진행, 패널(생산·입고), 출고·배차, 적재표
 * 왼쪽: 견적 목록 / 오른쪽: 고른 견적 상세
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BoardRow, JobState, PanelState, WorkDetail, Shipment, workBoard, workDetail, setJobState, panelAction, saveShipment, shipmentAction, errMsg,
} from '../utils/api';
import { smBtn, inp, chip, C } from './ui';

export const JOB: { st: JobState; label: string; color: string }[] = [
  { st: null, label: '견적', color: '#8B95A1' }, { st: 'CONFIRMED', label: '확정', color: '#3182F6' },
  { st: 'WORK', label: '작업 중', color: '#F76707' }, { st: 'WORKEND', label: '작업 완료', color: '#6D4C41' },
  { st: 'OUT', label: '출고', color: '#7B1FA2' }, { st: 'DONE', label: '설치 완료', color: '#03B26C' },
];
export const jobOf = (st: JobState) => JOB.find(j => j.st === (st || null)) ?? JOB[0];
export const PANEL: Record<PanelState, { label: string; color: string }> = {
  TODO: { label: '생산 전', color: '#8B95A1' }, MADE: { label: '생산 완료', color: '#F76707' }, STOCK: { label: '입고', color: '#1565C0' },
  OUT: { label: '출고', color: '#7B1FA2' }, DONE: { label: '설치', color: '#03B26C' },
};
const SHIP: Record<Shipment['STATE'], string> = { PLAN: '배차', LOADED: '적재 완료', DONE: '도착·설치' };
export const d8 = (s: string | null | undefined) => (s && /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : '');
const fmt = (s: string | null) => (s || '').replace('T', ' ').slice(0, 16);
/** 패널 번호 순서: 벽 → 칸막이 → 천장 → 바닥, 같은 종류는 숫자 순 (라벨과 같음) */
export const byPanelNo = (a: string, b: string) =>
  'WPCF'.indexOf(a[0]) - 'WPCF'.indexOf(b[0]) || a.localeCompare(b, undefined, { numeric: true });
const esc = (s: string) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

const btn = (bg: string, outline = false): React.CSSProperties => ({ ...smBtn(bg, !outline), height: 34, fontSize: 13 });
const input: React.CSSProperties = { ...inp, height: 34, fontSize: 13, minWidth: 0 };

/** 진행 막대: 생산·출고·설치 */
const Progress: React.FC<{ r: BoardRow }> = ({ r }) => {
  if (!r.N) return <span style={{ fontSize: 12, color: '#aaa' }}>작업지시 전</span>;
  const seg = (n: number, c: string) => <div style={{ width: `${(n / r.N) * 100}%`, background: c }} />;
  return (
    <div>
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: '#eee' }}>
        {seg(r.DONE, '#03B26C')}{seg(r.SHIPPED - r.DONE, '#7B1FA2')}{seg(r.STOCK, '#1565C0')}{seg(r.MADE - r.STOCK - r.SHIPPED, '#F76707')}
      </div>
      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>생산 {r.MADE}/{r.N} · 입고 {r.STOCK} · 출고 {r.SHIPPED} · 설치 {r.DONE}</div>
    </div>
  );
};

/** 적재표 인쇄 */
function printLoadSheet(d: WorkDetail, s: Shipment) {
  const ps = d.panels.filter(p => p.SHIP_ID === s.SHIP_ID).sort((a, b) => byPanelNo(a.PANEL_NO, b.PANEL_NO));
  const e = d.estimate;
  const rows = ps.map((p, i) => `<tr><td>${i + 1}</td><td class="b">${esc(p.PANEL_NO)}</td><td>${esc(p.KIND)}</td><td>${p.SIZE} × ${p.LENGTH}</td><td>${esc(p.JOINT || '')}</td><td>${esc(p.LOC || '')}</td><td class="chk">□</td><td class="chk">□</td></tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>적재표 - ${esc(e.ESTI_NO)}</title><style>
    @page { size: A4 portrait; margin: 10mm; } body { font-family: "Malgun Gothic", Arial, sans-serif; font-size: 12px; color: #000; }
    h1 { font-size: 20px; letter-spacing: 6px; margin: 0 0 8px; } table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #555; padding: 4px 6px; } th { background: #eee; } .b { font-weight: bold; } .chk { text-align: center; width: 40px; }
    .info td:nth-child(odd) { background: #f5f5f5; width: 80px; font-weight: bold; } .sign td { height: 46px; vertical-align: top; }
  </style></head><body>
  <h1>적재표</h1>
  <table class="info">
    <tr><td>견적번호</td><td>${esc(e.ESTI_NO)}</td><td>현장</td><td>${esc(e.SITE_NM || '')}</td></tr>
    <tr><td>출고일</td><td>${d8(s.SHIP_DT)}</td><td>도착지</td><td>${esc(s.DEST || '')}</td></tr>
    <tr><td>운송업체</td><td>${esc(s.CARRIER || '')}</td><td>차량번호</td><td>${esc(s.CAR_NO || '')}</td></tr>
    <tr><td>기사</td><td>${esc(s.DRIVER_NM || '')} ${esc(s.DRIVER_TEL || '')}</td><td>부자재</td><td>${esc(s.SUBMAT || '')}</td></tr>
    <tr><td>패널</td><td colspan="3"><b>${ps.length}장</b> — ${e.HORIZONTAL}×${e.VERTICAL}, ${e.PANEL_THICK}T ${esc(s.MEMO || '')}</td></tr>
  </table><br>
  <table><tr><th>#</th><th>패널 번호</th><th>구분</th><th>폭 × 길이</th><th>결합</th><th>보관 위치</th><th>적재</th><th>도착</th></tr>${rows}</table><br>
  <table class="sign"><tr><td>출고 확인</td><td>기사 확인</td><td>현장 인수</td></tr></table>
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('팝업이 차단되었습니다.'); return; }
  w.document.write(html); w.document.close();
}

const EMPTY_SHIP = { shipId: 0, shipDt: '', carrier: '', carNo: '', driverNm: '', driverTel: '', fee: '', dest: '', submat: '', memo: '' };

const Detail: React.FC<{ no: string; onChanged: () => void; onOpen: (no: string) => void }> = ({ no, onChanged, onOpen }) => {
  const [d, setD] = useState<WorkDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<PanelState | ''>('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [loc, setLoc] = useState('');
  const [ship, setShip] = useState<typeof EMPTY_SHIP | null>(null);
  const load = () => workDetail(no).then(r => { setD(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { setD(null); setSel(new Set()); setShip(null); load(); }, [no]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (f: () => Promise<unknown>) => { try { await f(); await load(); onChanged(); } catch (e) { setErr(errMsg(e)); } };
  const panels = useMemo(() => (d?.panels ?? []).filter(p => !filter || p.STATE === filter)
    .sort((a, b) => byPanelNo(a.PANEL_NO, b.PANEL_NO)), [d, filter]);
  if (!d) return <div style={{ padding: 20, color: err ? '#F04452' : '#888' }}>{err ?? '불러오는 중…'}</div>;
  const e = d.estimate, job = jobOf(e.JOB_STATE);
  const count = (s: PanelState) => d.panels.filter(p => p.STATE === s).length;
  const chosen = Array.from(sel);
  const act = (action: 'made' | 'in' | 'reset') => {
    if (!chosen.length) { setErr('패널을 고르세요'); return; }
    if (action === 'reset' && !window.confirm(`${chosen.length}장을 '생산 전'으로 되돌릴까요? 입고·출고 기록이 지워집니다.`)) return;
    run(() => panelAction(no, chosen, action, action === 'in' ? loc : undefined)).then(() => setSel(new Set()));
  };
  const sec: React.CSSProperties = { fontSize: 14, fontWeight: 'bold', margin: '18px 0 8px', color: '#333' };
  const th: React.CSSProperties = { textAlign: 'left', padding: '5px 6px', borderBottom: '1px solid #ddd', background: '#f5f5f5', position: 'sticky', top: 0 };
  const td: React.CSSProperties = { padding: '4px 6px', borderBottom: '1px solid #eee' };
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{e.SITE_NM || '(현장명 없음)'}</h2>
        <span style={{ color: '#666' }}>{e.ESTI_NO} · {e.HORIZONTAL}×{e.VERTICAL} · {e.PANEL_THICK}T</span>
        <button onClick={() => onOpen(no)} style={{ ...btn('#3182F6', true), marginLeft: 'auto' }}>도면 열기</button>
      </div>
      {/* 상태 단계 */}
      <div style={{ display: 'flex', gap: 4, margin: '12px 0', flexWrap: 'wrap' }}>
        {JOB.map((j, i) => {
          const on = JOB.indexOf(job) >= i;
          return <div key={j.label} style={{ flex: 1, minWidth: 70, textAlign: 'center', padding: '6px 4px', fontSize: 12, borderRadius: 4,
            background: on ? j.color : '#f0f0f0', color: on ? '#fff' : '#999', fontWeight: job === j ? 'bold' : 'normal' }}>{j.label}</div>;
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
        {!e.JOB_STATE && <button style={btn('#3182F6')} onClick={() => run(() => setJobState(no, 'CONFIRMED'))}>견적 확정</button>}
        {e.JOB_STATE === 'CONFIRMED' && <button style={btn('#8B95A1', true)} onClick={() => run(() => setJobState(no, null))}>확정 취소</button>}
        {!d.panels.length && <span style={{ color: '#F76707' }}>작업지시 전 — [도면 열기] → 위쪽 [작업지시]로 패널 목록을 만듭니다</span>}
        {e.ORDER_DT && <span style={{ color: '#555' }}>작업일 <b>{d8(e.WORK_DT) || '-'}</b> · 출고 예정 <b>{d8(e.DUE_DT) || '-'}</b> · 담당 <b>{e.WORK_MGR || '-'}</b>{e.WORK_MEMO && ` · ${e.WORK_MEMO}`}</span>}
        {e.JOB_STATE === 'OUT' && <button style={btn('#03B26C')} onClick={() => run(() => setJobState(no, 'DONE'))}>설치 완료 처리</button>}
      </div>
      {err && <div style={{ color: '#F04452', fontSize: 13, marginTop: 8 }}>⚠ {err}</div>}

      {d.panels.length > 0 && <>
        <div style={sec}>패널 {d.panels.length}장</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {(['', 'TODO', 'MADE', 'STOCK', 'OUT', 'DONE'] as const).map(s => (
            <button key={s || 'all'} onClick={() => { setFilter(s); setSel(new Set()); }} style={{
              padding: '3px 10px', fontSize: 12, borderRadius: 12, cursor: 'pointer',
              border: filter === s ? `2px solid ${s ? PANEL[s].color : '#333'}` : '1px solid #ccc', background: '#fff', color: s ? PANEL[s].color : '#333',
            }}>{s ? `${PANEL[s].label} ${count(s)}` : `전체 ${d.panels.length}`}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, padding: 8, background: '#fafafa', borderRadius: 6 }}>
          <span style={{ fontSize: 13 }}>선택 <b>{chosen.length}</b>장 →</span>
          <button style={btn('#F76707')} onClick={() => act('made')}>생산 완료</button>
          <input placeholder="보관 위치 (예: A-3)" value={loc} onChange={ev => setLoc(ev.target.value)} style={{ ...input, width: 130 }} />
          <button style={btn('#1565C0')} onClick={() => act('in')}>입고</button>
          <button style={btn('#8B95A1', true)} onClick={() => act('reset')}>생산 전으로</button>
        </div>
        <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              <th style={th}><input type="checkbox" checked={panels.length > 0 && panels.every(p => sel.has(p.PANEL_NO))}
                onChange={ev => setSel(ev.target.checked ? new Set(panels.map(p => p.PANEL_NO)) : new Set())} /></th>
              {['번호', '구분', '폭 × 길이', '결합', '상태', '위치', '배차', '최근'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>{panels.map(p => (
              <tr key={p.PANEL_NO} onClick={() => setSel(s => { const n = new Set(s); n.has(p.PANEL_NO) ? n.delete(p.PANEL_NO) : n.add(p.PANEL_NO); return n; })}
                style={{ cursor: 'pointer', background: sel.has(p.PANEL_NO) ? '#E8F3FF' : undefined }}>
                <td style={td}><input type="checkbox" readOnly checked={sel.has(p.PANEL_NO)} /></td>
                <td style={{ ...td, fontWeight: 'bold' }}>{p.PANEL_NO}</td>
                <td style={td}>{p.KIND}</td>
                <td style={td}>{p.SIZE} × {p.LENGTH}</td>
                <td style={td}>{p.JOINT}</td>
                <td style={{ ...td, color: PANEL[p.STATE].color, fontWeight: 'bold' }}>{PANEL[p.STATE].label}</td>
                <td style={td}>{p.LOC}</td>
                <td style={td}>{p.SHIP_ID ? d.shipments.find(s => s.SHIP_ID === p.SHIP_ID)?.CAR_NO || `#${p.SHIP_ID}` : ''}</td>
                <td style={{ ...td, color: '#888', fontSize: 11 }}>{fmt(p.UPT_DT)} {p.UPT_ID}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>

        <div style={{ ...sec, display: 'flex', alignItems: 'center', gap: 8 }}>
          출고·배차 {d.shipments.length}건
          <button style={btn('#7B1FA2')} onClick={() => setShip({ ...EMPTY_SHIP, shipDt: d8(e.DUE_DT) })}>+ 배차 추가</button>
        </div>
        {ship && (
          <div style={{ border: '1px solid #CE93D8', borderRadius: 6, padding: 10, marginBottom: 10, background: '#FBF5FC' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
              {([['shipDt', '출고일', 'date'], ['carrier', '운송업체'], ['carNo', '차량번호'], ['driverNm', '기사 이름'], ['driverTel', '기사 연락처'],
                ['fee', '운임(원)'], ['dest', '도착지'], ['submat', '부자재'], ['memo', '메모']] as [keyof typeof EMPTY_SHIP, string, string?][]).map(([k, l, type]) => (
                <label key={k} style={{ fontSize: 12, color: '#555' }}>{l}
                  <input type={type ?? 'text'} value={String(ship[k] ?? '')} onChange={ev => setShip({ ...ship, [k]: ev.target.value })} style={{ ...input, width: '100%', marginTop: 2 }} />
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button style={btn('#8B95A1', true)} onClick={() => setShip(null)}>취소</button>
              <button style={btn('#7B1FA2')} onClick={() => run(() => saveShipment(no, ship)).then(() => setShip(null))}>{ship.shipId ? '고치기' : '배차 저장'}</button>
            </div>
          </div>
        )}
        {d.shipments.map(s => {
          const loaded = d.panels.filter(p => p.SHIP_ID === s.SHIP_ID).length;
          const stock = d.panels.filter(p => p.STATE === 'STOCK' || p.STATE === 'MADE');
          const toLoad = chosen.length ? chosen : stock.map(p => p.PANEL_NO);
          return (
            <div key={s.SHIP_ID} style={{ border: '1px solid #eee', borderRadius: 6, padding: 10, marginBottom: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <b style={{ color: '#7B1FA2' }}>{SHIP[s.STATE]}</b>
                <span>{d8(s.SHIP_DT) || '날짜 미정'} · {s.CAR_NO || '차량 미정'} · {s.CARRIER || ''} · {s.DRIVER_NM || ''} {s.DRIVER_TEL || ''}</span>
                {s.FEE != null && <span>· 운임 {Number(s.FEE).toLocaleString()}원</span>}
                <span>· 적재 <b>{loaded}</b>장</span>
              </div>
              {(s.DEST || s.SUBMAT || s.MEMO) && <div style={{ color: '#666', marginTop: 4 }}>{[s.DEST && `도착지 ${s.DEST}`, s.SUBMAT && `부자재 ${s.SUBMAT}`, s.MEMO].filter(Boolean).join(' · ')}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {s.STATE !== 'DONE' && toLoad.length > 0 && <button style={btn('#7B1FA2')}
                  onClick={() => run(() => shipmentAction(no, s.SHIP_ID, 'load', toLoad)).then(() => setSel(new Set()))}>
                  {chosen.length ? `고른 ${chosen.length}장 싣기` : `생산·입고된 ${stock.length}장 모두 싣기`}</button>}
                {loaded > 0 && <button style={btn('#333', true)} onClick={() => printLoadSheet(d, s)}>적재표 인쇄</button>}
                {s.STATE === 'LOADED' && <button style={btn('#03B26C')} onClick={() => run(() => shipmentAction(no, s.SHIP_ID, 'done'))}>도착·설치 완료</button>}
                {s.STATE !== 'DONE' && <button style={btn('#8B95A1', true)} onClick={() => setShip({
                  shipId: s.SHIP_ID, shipDt: d8(s.SHIP_DT), carrier: s.CARRIER || '', carNo: s.CAR_NO || '', driverNm: s.DRIVER_NM || '', driverTel: s.DRIVER_TEL || '',
                  fee: s.FEE != null ? String(s.FEE) : '', dest: s.DEST || '', submat: s.SUBMAT || '', memo: s.MEMO || '' })}>고치기</button>}
                {s.STATE !== 'DONE' && <button style={btn('#F04452', true)} onClick={() => {
                  if (window.confirm('이 배차를 취소할까요? 실린 패널은 다시 입고 상태가 됩니다.')) run(() => shipmentAction(no, s.SHIP_ID, 'delete'));
                }}>배차 취소</button>}
              </div>
            </div>
          );
        })}
      </>}
    </div>
  );
};

const WorkBoard: React.FC<{ onClose: () => void; onOpen: (no: string) => void; initial?: string | null }> = ({ onClose, onOpen, initial }) => {
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<string>('active');
  const [cur, setCur] = useState<string | null>(initial ?? null);
  const [err, setErr] = useState<string | null>(null);
  const load = (query = q) => workBoard(query).then(r => { setRows(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (rows ?? []).filter(r => tab === 'all' ? true : tab === 'active' ? r.JOB_STATE !== 'DONE' : (r.JOB_STATE || 'EST') === tab);
  const tabs: [string, string][] = [['active', '진행 중'], ['EST', '견적'], ['CONFIRMED', '확정'], ['WORK', '작업 중'], ['WORKEND', '작업 완료'], ['OUT', '출고'], ['DONE', '설치 완료'], ['all', '전체']];
  const today = new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10).replace(/-/g, '');
  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 1500, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#fff', borderBottom: `1px solid ${C.line}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 18, color: C.text, letterSpacing: -0.3 }}>작업 현황</b>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(q)} placeholder="현장명·견적번호 검색"
          style={{ ...input, width: 240 }} />
        <button onClick={() => load(q)} style={btn('#3182F6')}>검색</button>
        <button onClick={onClose} style={{ ...btn('#757575'), marginLeft: 'auto' }}>닫기</button>
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 0, flexWrap: 'wrap' }}>
        <div style={{ width: 420, maxWidth: '100%', borderRight: `1px solid ${C.line}`, background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', gap: 6, padding: '12px 12px', flexWrap: 'wrap', borderBottom: `1px solid ${C.fill}` }}>
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={{ ...chip(tab === k), height: 30, padding: '0 11px', fontSize: 12 }}>
                {l} {k === 'active' || k === 'all' ? '' : (rows ?? []).filter(r => (r.JOB_STATE || 'EST') === k).length}
              </button>
            ))}
          </div>
          {err && <div style={{ color: '#F04452', padding: 8, fontSize: 13 }}>⚠ {err}</div>}
          <div style={{ overflow: 'auto', flex: 1 }}>
            {rows === null ? <div style={{ padding: 16, color: '#888' }}>불러오는 중…</div>
              : shown.length === 0 ? <div style={{ padding: 16, color: '#888' }}>없습니다 — 도면을 저장하면 여기에 나옵니다</div>
              : shown.map(r => {
                const j = jobOf(r.JOB_STATE);
                const late = r.DUE_DT && r.DUE_DT < today && r.JOB_STATE !== 'OUT' && r.JOB_STATE !== 'DONE';
                return (
                  <div key={r.ESTI_NO} onClick={() => setCur(r.ESTI_NO)} style={{ padding: '12px 14px', borderBottom: `1px solid ${C.fill}`, cursor: 'pointer',
                    background: cur === r.ESTI_NO ? '#E8F3FF' : undefined }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: j.color, color: '#fff', fontWeight: 600 }}>{j.label}</span>
                      <b style={{ fontSize: 14, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.SITE_NM || '(현장명 없음)'}</b>
                      {r.DUE_DT && <span style={{ fontSize: 12, color: late ? '#F04452' : '#555', fontWeight: late ? 'bold' : 'normal' }}>출고 {d8(r.DUE_DT).slice(5)}{late ? ' 지연' : ''}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#777', margin: '3px 0 5px' }}>{r.ESTI_NO} · {r.HORIZONTAL}×{r.VERTICAL} · {r.PANEL_THICK}T{r.WORK_MGR ? ` · ${r.WORK_MGR}` : ''}</div>
                    <Progress r={r} />
                  </div>
                );
              })}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 320, overflow: 'auto', minHeight: 0, height: '100%' }}>
          {cur ? <Detail no={cur} onChanged={() => load()} onOpen={onOpen} /> : <div style={{ padding: 24, color: '#888' }}>왼쪽에서 견적을 고르세요</div>}
        </div>
      </div>
    </div>
  );
};

export default WorkBoard;
